import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, TrendingUp, TrendingDown, DollarSign, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_METHODS = ['Μετρητά', 'Κάρτα'];

function DashboardTab() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total_income: 0, total_expenses: 0, profit: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    type: 'income',
    amount: '',
    description: '',
    payment_method: ''
  });

  // Date filters
  const [dateFilter, setDateFilter] = useState('today'); // 'today', 'week', 'month', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const getDateRange = () => {
    const now = new Date();
    let start, end;

    switch (dateFilter) {
      case 'today':
        start = end = now.toISOString().split('T')[0];
        break;
      case 'week':
        // Calculate Monday of current week
        const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday needs to go back 6 days
        
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - daysFromMonday); // Go back to Monday
        
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday (Monday + 6 days)
        
        start = weekStart.toISOString().split('T')[0];
        end = weekEnd.toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'custom':
        start = customStartDate;
        end = customEndDate;
        break;
      default:
        start = end = now.toISOString().split('T')[0];
    }

    return { start, end };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/finances`, {
          params: { start_date: start, end_date: end },
          withCredentials: true
        }),
        axios.get(`${API}/admin/finances/stats`, {
          params: { start_date: start, end_date: end },
          withCredentials: true
        })
      ]);

      setEntries(entriesRes.data.entries || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateFilter, customStartDate, customEndDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        date: new Date(formData.date).toISOString(),
        type: formData.type,
        amount: parseFloat(formData.amount),
        description: formData.description,
        payment_method: formData.payment_method || null
      };

      if (editMode && currentEntry) {
        await axios.put(`${API}/admin/finances/${currentEntry.finance_id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/finances`, data, { withCredentials: true });
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving finance entry:', error);
    }
  };

  const handleEdit = (entry) => {
    setCurrentEntry(entry);
    setFormData({
      date: entry.date.split('T')[0],
      type: entry.type,
      amount: entry.amount.toString(),
      description: entry.description,
      payment_method: entry.payment_method || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/admin/finances/${deleteId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      console.error('Error deleting finance entry:', error);
    } finally {
      setDeleteId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      type: 'income',
      amount: '',
      description: '',
      payment_method: ''
    });
    setEditMode(false);
    setCurrentEntry(null);
  };

  if (loading) {
    return <div className="text-center text-gray-400 py-12">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-8" data-testid="dashboard-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard 
          icon={TrendingUp} 
          label="Συνολικά Έσοδα" 
          value={`${stats.total_income.toFixed(2)}€`}
          color="text-green-400"
          bgColor="bg-green-500/10"
        />
        <StatCard 
          icon={TrendingDown} 
          label="Συνολικά Έξοδα" 
          value={`${stats.total_expenses.toFixed(2)}€`}
          color="text-red-400"
          bgColor="bg-red-500/10"
        />
        <StatCard 
          icon={DollarSign} 
          label="Καθαρό Κέρδος/Ζημία" 
          value={`${stats.profit.toFixed(2)}€`}
          color={stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}
          bgColor={stats.profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}
        />
      </div>

      {/* Filters and Actions */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center">
        {/* Date Filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setDateFilter('today')}
            className={`px-4 py-2 text-sm font-light tracking-wide transition-all border ${
              dateFilter === 'today' 
                ? 'border-white text-white bg-white/10' 
                : 'border-white/20 text-gray-400 hover:border-white/40'
            }`}
          >
            Σήμερα
          </button>
          <button
            onClick={() => setDateFilter('week')}
            className={`px-4 py-2 text-sm font-light tracking-wide transition-all border ${
              dateFilter === 'week' 
                ? 'border-white text-white bg-white/10' 
                : 'border-white/20 text-gray-400 hover:border-white/40'
            }`}
          >
            Εβδομάδα
          </button>
          <button
            onClick={() => setDateFilter('month')}
            className={`px-4 py-2 text-sm font-light tracking-wide transition-all border ${
              dateFilter === 'month' 
                ? 'border-white text-white bg-white/10' 
                : 'border-white/20 text-gray-400 hover:border-white/40'
            }`}
          >
            Μήνας
          </button>
          <button
            onClick={() => setDateFilter('custom')}
            className={`px-4 py-2 text-sm font-light tracking-wide transition-all border ${
              dateFilter === 'custom' 
                ? 'border-white text-white bg-white/10' 
                : 'border-white/20 text-gray-400 hover:border-white/40'
            }`}
          >
            Custom
          </button>
        </div>

        {/* Custom Date Range */}
        {dateFilter === 'custom' && (
          <div className="flex gap-2 items-center">
            <Input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
            />
            <span className="text-gray-400">έως</span>
            <Input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
        )}

        {/* Add Button */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Νέα Εγγραφή
            </Button>
          </DialogTrigger>
          <DialogContent 
            className="max-w-lg"
            style={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 99999,
              backgroundColor: '#000',
              border: '2px solid #fff',
              padding: '2rem',
              opacity: 1,
              visibility: 'visible',
              display: 'grid',
              width: '90%',
              maxWidth: '500px'
            }}
          >
            <DialogHeader>
              <p className="premium-modal-eyebrow">DIMAKAS Dashboard</p>
              <DialogTitle>
                {editMode ? 'Επεξεργασία Εγγραφής' : 'Νέα Εγγραφή'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                className="premium-modal-input border-white/15 text-white"
              />
              
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-white cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    value="income"
                    checked={formData.type === 'income'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="accent-green-500"
                  />
                  <span>Έσοδο</span>
                </label>
                <label className="flex items-center gap-2 text-white cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    value="expense"
                    checked={formData.type === 'expense'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="accent-red-500"
                  />
                  <span>Έξοδο</span>
                </label>
              </div>

              <Input
                type="number"
                step="0.01"
                placeholder="Ποσό (€)"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                className="premium-modal-input border-white/15 text-white placeholder:text-gray-500"
              />

              <Textarea
                placeholder="Περιγραφή"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="premium-modal-input min-h-[80px] border-white/15 text-white placeholder:text-gray-500"
              />

              <select
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full bg-white/5 border border-white/15 text-white px-3 py-2 focus:outline-none focus:border-white/30"
              >
                <option value="">Μέθοδος Πληρωμής (προαιρετικό)</option>
                {PAYMENT_METHODS.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>

              <div className="flex justify-end space-x-2 pt-1">
                <Button type="button" variant="outline" className="premium-modal-secondary" onClick={() => setDialogOpen(false)}>
                  Ακύρωση
                </Button>
                <Button type="submit" className="premium-modal-primary">
                  Αποθήκευση
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Entries List - Desktop: Table, Mobile: Cards */}
      
      {/* Desktop Table (hidden on mobile) */}
      <div className="hidden md:block border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Ημερομηνία</th>
              <th className="text-left px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Τύπος</th>
              <th className="text-left px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Περιγραφή</th>
              <th className="text-right px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Ποσό</th>
              <th className="text-left px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Μέθοδος</th>
              <th className="text-right px-4 py-3 text-xs font-light tracking-widest uppercase text-gray-400">Ενέργειες</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-gray-500 py-12 font-light">
                  Δεν υπάρχουν εγγραφές για την επιλεγμένη περίοδο
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.finance_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 text-gray-300 text-sm font-light">
                    {new Date(entry.date).toLocaleDateString('el-GR')}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-light px-2 py-1 rounded ${
                      entry.type === 'income' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {entry.type === 'income' ? (
                        <>
                          <TrendingUp className="w-3 h-3" />
                          Έσοδο
                        </>
                      ) : (
                        <>
                          <TrendingDown className="w-3 h-3" />
                          Έξοδο
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-white text-sm font-light">
                    {entry.description}
                  </td>
                  <td className={`px-4 py-3 text-right text-sm font-medium ${
                    entry.type === 'income' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {entry.type === 'income' ? '+' : '-'}{entry.amount.toFixed(2)}€
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-sm font-light">
                    {entry.payment_method || '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(entry.finance_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards (hidden on desktop) */}
      <div className="md:hidden space-y-4">
        {entries.length === 0 ? (
          <div className="text-center text-gray-500 py-12 font-light border border-white/10 bg-white/5">
            Δεν υπάρχουν εγγραφές για την επιλεγμένη περίοδο
          </div>
        ) : (
          entries.map((entry) => (
            <div key={entry.finance_id} className="border border-white/10 bg-white/5 p-4 space-y-3">
              {/* Header Row */}
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="text-gray-400 text-xs font-light">
                    {new Date(entry.date).toLocaleDateString('el-GR')}
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs font-light px-2 py-1 rounded ${
                    entry.type === 'income' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {entry.type === 'income' ? (
                      <>
                        <TrendingUp className="w-3 h-3" />
                        Έσοδο
                      </>
                    ) : (
                      <>
                        <TrendingDown className="w-3 h-3" />
                        Έξοδο
                      </>
                    )}
                  </span>
                </div>
                <div className={`text-lg font-medium ${
                  entry.type === 'income' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {entry.type === 'income' ? '+' : '-'}{entry.amount.toFixed(2)}€
                </div>
              </div>

              {/* Description */}
              <div className="text-white text-sm font-light">
                {entry.description}
              </div>

              {/* Payment Method */}
              {entry.payment_method && (
                <div className="text-gray-400 text-xs font-light flex items-center gap-2">
                  <span className="text-gray-500">Μέθοδος:</span>
                  {entry.payment_method}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-white/10">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleEdit(entry)}
                  className="flex-1 text-xs"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Επεξεργασία
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setDeleteId(entry.finance_id)}
                  className="flex-1 text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Διαγραφή
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={open => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent 
          className="max-w-md"
          style={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 99999,
            backgroundColor: '#000',
            border: '2px solid #fff',
            padding: '2rem',
            opacity: 1,
            visibility: 'visible',
            display: 'grid',
            width: '90%',
            maxWidth: '400px'
          }}
        >
          <AlertDialogHeader>
            <p className="premium-modal-eyebrow">DIMAKAS Dashboard</p>
            <AlertDialogTitle>Διαγραφή Εγγραφής</AlertDialogTitle>
            <AlertDialogDescription>
              Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την εγγραφή;
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="premium-modal-danger">
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color = 'text-white', bgColor = 'bg-white/5' }) {
  return (
    <div className={`${bgColor} border border-white/10 p-6 space-y-3`}>
      <div className="flex items-center gap-3">
        <div className={`${color} p-2 bg-black/30 rounded`}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="text-gray-400 text-xs font-light tracking-widest uppercase">{label}</p>
      </div>
      <p className={`text-3xl font-light ${color}`}>{value}</p>
    </div>
  );
}

export default DashboardTab;
